import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { IAllowedOperations } from '../../../users/models/userInfo.interface';
import { IDocumentSummaryInfo } from '../../../message-models/document-summary-info.interface';
import { Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { ExportExcelService } from '../../../common/services/export-excel.service';
import { DocumentEntityType, DocumentEntityTypeString } from '../../../message-models/document-entity-type.enum';
import { IEntityDocumentSummary } from '../../../message-models/entity-document-summary';
import { MenuItem } from 'primeng/api';

@Component({
  selector: 'app-operative-details-documents-list',
  templateUrl: './operative-details-documents-list.component.html',
  styleUrls: ['./operative-details-documents-list.component.scss']
})

export class OperativeDetailsDocumentsListComponent implements OnInit {
  documentEntityType = DocumentEntityType;
  @Input() showTableCaption: boolean;
  @Input() showHeaderText = true;
  @Input() showCount = false;
  @Input() showRowControllerButtons: boolean;
  @Input() headerText = 'Operative Documents';
  @Input() addDocumentButtonText = 'Add';
  @Input() showAddDocumentButton = true;
  @Input() showWarningMessage: boolean = false;
  isOnMobile = false;
  @Input() allowedOperations: IAllowedOperations;
  DocumentEntityTypeString: typeof DocumentEntityTypeString = DocumentEntityTypeString;
  @Input() operativeDocuments: Array<IDocumentSummaryInfo> = new Array<IDocumentSummaryInfo>();
  @Output() openDocument: EventEmitter<IDocumentSummaryInfo> = new EventEmitter<IDocumentSummaryInfo>();
  @Output() editDocument: EventEmitter<IDocumentSummaryInfo> = new EventEmitter<IDocumentSummaryInfo>();
  @Output() downloadDocument: EventEmitter<IDocumentSummaryInfo> = new EventEmitter<IDocumentSummaryInfo>();
  @Output() deleteDocument: EventEmitter<IDocumentSummaryInfo> = new EventEmitter<IDocumentSummaryInfo>();
  @Output() viewDocument: EventEmitter<IDocumentSummaryInfo> = new EventEmitter<IDocumentSummaryInfo>();
  @Output() addDocument: EventEmitter<void> = new EventEmitter<void>();
  @Output() addObservationClicked: EventEmitter<IEntityDocumentSummary> = new EventEmitter<IEntityDocumentSummary>();
  @Output() refreshDocumentsRequested: EventEmitter<boolean> = new EventEmitter<boolean>();

  DocumentSummariesToExport: any[] = [];
  columns: any[];
  exportColumns: any[];
  expiredFilter = { includeExpired: false };
  rowMenuItems: MenuItem[];

  constructor(private router: Router, private datePipe: DatePipe, private exportExcelService: ExportExcelService) {
  }

  ngOnInit(): void {
    this.isOnMobile = window.outerWidth <= 480;
    this.columns =
      [{ field: 'entityType', header: 'Type', type: 'entityTypeString' },
      { field: 'documentType', header: 'Document Type', type: 'text' },
      { field: 'originalFilename', header: 'Original Filename', type: 'text' },
      { field: 'documentCreationDate', header: 'Created On', type: 'date' },
      { field: 'expiryDate', header: 'Expires On', type: 'date' },
      { field: 'reviewDate', header: 'Review Due On', type: 'date' },
      { field: 'totalObservations', header: 'Observations', type: 'text' },
      ];
    this.exportColumns = [
      { field: 'entityTypeString', header: 'Type', type: 'text' },
      { field: 'operativeFullName', header: 'Operative Full Name', type: 'link' },
      { field: 'documentType', header: 'Document Type', type: 'text' },
      { field: 'originalFilename', header: 'Original Filename', type: 'text' },
      { field: 'documentCreationDate', header: 'Created On', type: 'date' },
      { field: 'expiryDate', header: 'Expires On', type: 'date' },
      { field: 'reviewDate', header: 'Review Due On', type: 'date' },
      { field: 'totalObservations', header: 'Observations', type: 'text' },
    ];

    this.rowMenuItems = [
      {
        label: 'Preview',
        icon: 'fas fa-search',
        state: { 'contextLogic': 'canPreviewDocument' },
        styleClass: 'eca-menu-item-green',
        command: ($event) => this.openDocumentClicked($event)
      },
      {
        label: 'Download',
        icon: 'fas fa-download',
        styleClass: 'eca-menu-item-green',
        command: ($event) => this.downloadDocumentClicked($event)
      },
      {
        label: 'Add Observation',
        icon: 'fas fa-eye',
        visible: this.showRowControllerButtons,
        styleClass: 'eca-menu-item-green',
        command: ($event) => this.addObservationToADocumentClicked($event)
      },
      {
        label: 'Remove',
        icon: 'fas fa-trash-alt',
        visible: this.allowedOperations?.canDelete,
        styleClass: 'eca-menu-item-red',
        command: ($event) => this.deleteDocumentClicked($event)
      }
    ]
  }

  openDocumentClicked(operativeDocumentSummary: IDocumentSummaryInfo) {
    this.openDocument.emit(operativeDocumentSummary);
  }

  deleteDocumentClicked(operativeDocumentSummary: IDocumentSummaryInfo) {
    this.deleteDocument.emit(operativeDocumentSummary);
  }

  downloadDocumentClicked(operativeDocumentSummary: IDocumentSummaryInfo) {
    this.downloadDocument.emit(operativeDocumentSummary);
  }

  editDocumentClicked(operativeDocumentSummary: IDocumentSummaryInfo) {
    this.editDocument.emit(operativeDocumentSummary);
  }

  addDocumentClicked() {
    this.addDocument.emit();
  }

  viewDocumentClicked(operativeDocumentSummary: IDocumentSummaryInfo) {
    if (operativeDocumentSummary.entityType === DocumentEntityType.employedPerson) {
      this.router.navigate(['/operative-document/', operativeDocumentSummary.documentId]);

    } else if (operativeDocumentSummary.entityType === DocumentEntityType.trainingRecord) {
      this.router.navigate(['/training-record-document/', operativeDocumentSummary.documentId]);
    }
    else if (operativeDocumentSummary.entityType === DocumentEntityType.observation) {
      this.router.navigate(['/observation-document/', operativeDocumentSummary.documentId]);
    }
  }

  expiredFilterChanged() {
    this.refreshDocumentsRequested.emit(this.expiredFilter.includeExpired);
  }

  exportToExcel() {
    this.DocumentSummariesToExport = [];
    this.operativeDocuments.forEach(element => {
      const tempObject = {
        entityTypeString: element.entityTypeString,
        operativeFullName: element.operativeFullName,
        documentType: element.documentType,
        originalFilename: element.originalFilename,
        documentCreationDate: this.datePipe.transform(element.documentCreationDate?.dateTime, 'dd/MM/yyyy'),
        reviewDate: this.datePipe.transform(element.reviewDate?.dateTime, 'dd/MM/yyyy'),
        expiryDate: this.datePipe.transform(element.expiryDate?.dateTime, 'dd/MM/yyyy'),
        totalObservations: element.totalObservations
      };
      this.DocumentSummariesToExport.push(tempObject);
    });
    this.exportExcelService.exportToExcel(this.DocumentSummariesToExport, this.exportColumns, 'AllOperativeDocuments');
  }

  addObservationToADocumentClicked(entityDocument: IDocumentSummaryInfo) {
    this.addObservationClicked.emit(entityDocument);
  }
}
